import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { config } from 'src/config';
import { addExpiryTimeRange } from 'src/utils/utils';
import { IBackendState } from 'src/features/translations/interfaces';
import { RootState } from 'src/configuration/setup/store';

const BASE = config.backend.ACCESS_TOKEN_SERVICE;
const PATH = config.backend.ACCESS_TOKEN_PATH;
const fetchRequest = async () =>
    fetch(`${BASE}${PATH}`, {
        method: 'GET',
    });
// fetch initial token from backend
export const fetchToken = createAsyncThunk('auth/fetchToken', async (_, { rejectWithValue }) => {
    try {
        const resp = await fetchRequest();
        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            return rejectWithValue({ status: resp.status, message: text });
        }
        const data = await resp.json();
        data.expires_in = 600; // 10 sec for testing only
        return data; // { access_token, expires_in }
    } catch (err) {
        if (err instanceof Error) {
            return rejectWithValue({ message: err.message });
        }
        return rejectWithValue({ message: String(err) });
    }
});

// optional explicit refresh action you can call if required
export const refreshToken = createAsyncThunk('auth/refreshToken', async (_, { rejectWithValue }) => {
    try {
        const resp = await fetchRequest();
        if (!resp.ok) {
            const text = await resp.text().catch(() => '');
            return rejectWithValue({ status: resp.status, message: text });
        }
        return await resp.json();
        // const data = await resp.json();
        // return data; // { access_token, expires_in }
    } catch (err) {
        if (err instanceof Error) {
            return rejectWithValue({ message: err.message });
        }
        return rejectWithValue({ message: String(err) });
    }
});

const initialState: IBackendState = {
    accessToken: '',
    expiresAt: null, // ms epoch
    status: 'idle',
    error: '',
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials: (state, action) => {
            const { accessToken, expiresAt } = action.payload;
            state.accessToken = accessToken;
            state.expiresAt = expiresAt;
            state.status = 'succeeded';
            state.error = '';
        },
        clearCredentials: (state) => {
            state.accessToken = '';
            state.expiresAt = 0;
            state.status = 'idle';
            state.error = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchToken.pending, (s) => {
                s.status = 'loading';
                s.error = null;
            })
            .addCase(fetchToken.fulfilled, (s, action) => {
                const { access_token, expires_in } = action.payload;
                s.accessToken = access_token;
                s.expiresAt = addExpiryTimeRange(expires_in); // Date.now() + expires_in * 1000;
                s.status = 'succeeded';
            })
            .addCase(fetchToken.rejected, (s, action) => {
                s.status = 'failed';
                s.error = action.payload || action.error;
                s.accessToken = null;
                s.expiresAt = null;
            })
            .addCase(refreshToken.fulfilled, (s, action) => {
                const { access_token, expires_in } = action.payload;
                s.accessToken = access_token;
                s.expiresAt = addExpiryTimeRange(expires_in); // Date.now() + expires_in * 1000;
                s.status = 'succeeded';
            })
            .addCase(refreshToken.rejected, (s, action) => {
                s.status = 'failed';
                s.error = action.payload || action.error;
                s.accessToken = null;
                s.expiresAt = null;
            });
    },
});
export const getAuthAccessToken = (state: RootState) => state.auth.accessToken;

export const { setCredentials, clearCredentials } = authSlice.actions;
export default authSlice.reducer;
